"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ExternalLink, Trophy } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { arenaApi, type ArenaLeaderboard } from "@/shared/api/arena";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Badge } from "@/shared/ui/data-display/badge";
import {
  TournamentPodium,
  type PodiumEntry,
} from "../tournament-podium";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";

interface Props {
  tournamentId: string;
  isManager: boolean;
  /** Турнир завершён — показываем пьедестал победителей над таблицей. */
  isCompleted?: boolean;
}

function errMsg(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    fallback
  );
}

export function ArenaLeaderboard({ tournamentId, isManager, isCompleted }: Props) {
  const tUi = useTranslations("Ui");
  const qc = useQueryClient();
  const queryKey = ["arena-leaderboard", tournamentId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => arenaApi.getLeaderboard(tournamentId),
  });

  const scoreM = useMutation({
    mutationFn: (v: { rosterId: string; round: number; points: number }) =>
      arenaApi.setScore(tournamentId, v.rosterId, v.round, v.points),
    onSuccess: (d) => qc.setQueryData(queryKey, d),
    onError: (e) => toast.error(errMsg(e, tUi("litArenaLeaderboard.neUdalosSohranitBally"))),
  });
  const roundM = useMutation({
    mutationFn: (round: number) => arenaApi.setCurrentRound(tournamentId, round),
    onSuccess: (d) => qc.setQueryData(queryKey, d),
    onError: (e) => toast.error(errMsg(e, tUi("litArenaLeaderboard.neUdalosSmenitRaund"))),
  });

  if (isLoading || !data || data.rows.length === 0) return null;

  const rounds = Array.from({ length: data.rounds }, (_, i) => i + 1);
  const showLobby = data.lobbyCount > 1;

  // Пьедестал: топ-3 команды (по рангу) с полным составом — только когда завершён.
  const podiumEntries: PodiumEntry[] =
    isCompleted && data.rows.some((r) => r.totalPoints > 0)
      ? data.rows
          .filter((r) => r.rank <= 3)
          .map((r) => ({
            place: r.rank,
            name: r.name,
            tag: r.tag,
            score: String(r.totalPoints),
            members: r.members.map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
              avatarUrl: m.avatarUrl,
            })),
          }))
      : [];

  return (
    <div className="space-y-5">
      {podiumEntries.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Trophy className="h-4 w-4 text-yellow-500" />
            {tUi("arenaLeaderboard.prizery")}
          </div>
          <TournamentPodium entries={podiumEntries} />
        </div>
      ) : null}
      <Card className="border-0 shadow-none rounded-2xl bg-black/6 dark:bg-white/4">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            {tUi("arenaLeaderboard.liderbord")}
          </span>
          <div className="flex items-center gap-3">
            {data.tasksDocUrl && (
              <a
                href={data.tasksDocUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {tUi("arenaLeaderboard.zadaniya")}
              </a>
            )}
            {isManager && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>{tUi("arenaLeaderboard.aktivnyyRaund")}</span>
                <Select
                  value={String(data.currentRound)}
                  disabled={roundM.isPending}
                  onValueChange={(v) => roundM.mutate(Number(v))}
                >
                  <SelectTrigger
                    className="h-7 w-[64px] text-xs"
                    aria-label={tUi("arenaLeaderboard.aktivnyyRaund")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">—</SelectItem>
                    {rounds.map((r) => (
                      <SelectItem key={r} value={String(r)}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-3">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">{tUi("arenaLeaderboard.komanda")}</th>
              {rounds.map((r) => (
                <th
                  key={r}
                  className={`whitespace-nowrap px-2 py-1.5 text-center ${
                    r === data.currentRound ? "text-primary" : ""
                  }`}
                >
                  Раунд&nbsp;{r}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right">Σ</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.rosterId} className="border-t border-border/60">
                <td className="px-2 py-1.5 font-mono text-muted-foreground">{row.rank}</td>
                <td className="px-2 py-1.5">
                  <span className="flex items-center gap-2">
                    <Link
                      href={`/rosters/${row.rosterId}`}
                      className="truncate font-medium hover:underline"
                    >
                      {row.name}
                    </Link>
                    <span className="font-mono text-xs text-muted-foreground">{row.tag}</span>
                    {showLobby && (
                      <Badge variant="outline" className="text-[10px]">
                        Л{row.lobbyNumber}
                      </Badge>
                    )}
                  </span>
                </td>
                {rounds.map((r) => {
                  const val = row.pointsByRound[String(r)] ?? 0;
                  return (
                    <td key={r} className="px-1 py-1 text-center">
                      {isManager ? (
                        <input
                          type="number"
                          defaultValue={val}
                          aria-label={tUi("litArenaLeaderboard.ochkiRaund", { round: r })}
                          key={`${row.rosterId}-${r}-${val}`}
                          className="w-14 rounded-md border border-input bg-background px-1.5 py-1 text-center text-sm"
                          onBlur={(e) => {
                            const next = Math.trunc(Number(e.target.value) || 0);
                            if (next !== val)
                              scoreM.mutate({ rosterId: row.rosterId, round: r, points: next });
                          }}
                        />
                      ) : (
                        <span className="tabular-nums text-muted-foreground">{val}</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                  {row.totalPoints}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
    </div>
  );
}
